import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
	Tooltip,
	notification,
} from 'antd';

import { Search, Button, DataGrid } from '@hydrodigital/design-system';
import {
	Combobox,
	ComboboxInput,
	ComboboxContent,
	ComboboxList,
	ComboboxItem,
	ComboboxEmpty,
} from '@hydrodigital/combobox';
import useDebounce from '@/hooks/useDebounce';
import UserService from '../../domain/Users/UserService';
import ModalUser from './ModalUser';
import ConfirmationModal from '../../components/Modal/ConfirmationModal';
import dayjs from 'dayjs';
import { AllUser } from '@/domain/Users/Users';

const userService = new UserService();

interface DataType {
	userId: string;
	firstName: string;
	lastName: string;
	role: string;
	roleId: string;
	inUse: boolean;
	timeZone: string;
	organizationId: string;
	organization: string;
	createdAt: string;
	createdBy: string;
	lastAccess: string;
}

/**
 * @author
 * @function @UserView
 **/

const UserView: FC = () => {
	const [searchValue, setSearchValue] = useState<string>('');
	const debouncedName = useDebounce(searchValue, 500);
	const [roles, setRoles] = useState<any>(null);
	const [modules, setModules] = useState<any>(null);
	const [organizations, setOrganizations] = useState<any>(null);
	const [role, setRole] = useState<any>(null);
	const [organization, setOrganization] = useState<any>(null);
	const [users, setUsers] = useState<any | null>([]);
	const [addModalUser, setAddModalUser] = useState<boolean>(false);
	const [showConfirmDelete, setShowConfirmDelete] = useState(false);
	const [selectedUser, setSelectedUser] = useState<AllUser | null>(null);
	const [isNewUser, setIsNewUser] = useState<boolean>(true);
	const [paginationState, setPaginationState] = useState({
		pageIndex: 0,
		pageSize: 10,
	});
	const [totalRows, setTotalRows] = useState(0);
	const securityData = JSON.parse(localStorage.getItem('security') || '{}');
	const userRole = securityData.role;
	const loggedUserId = localStorage.getItem('userId');
	const prevFiltersRef = useRef({ debouncedName, role });

	const gridColumns = [
		{
			header: 'User Name',
			id: 'userName',
			cell: ({ row }: any) => {
				const record = row.original as DataType;
				return <>{`${record.firstName} ${record.lastName}`}</>;
			},
		},
		{
			header: 'Role',
			accessorKey: 'role',
			cell: ({ cell }: any) => <span className="table-data-gis">{cell.getValue()}</span>,
		},
		{
			header: 'Time Zone',
			id: 'timeZone',
			cell: ({ row }: any) => {
				const tz = (row.original as DataType).timeZone;
				const display = `UTC${tz.indexOf('-') === 0 ? tz : '+' + tz}:00`;
				return <span className="table-data-gis">{display}</span>;
			},
		},
		{
			header: 'Organization',
			accessorKey: 'organization',
			cell: ({ cell }: any) => <span className="table-data-gis">{cell.getValue()}</span>,
		},
		{
			header: 'Enroll',
			id: 'enroll',
			cell: ({ row }: any) => {
				const record = row.original as DataType;
				return (
					<div className="flex column">
						<span>
							{dayjs(record.createdAt).format('YYYY-MM-DD')} at{' '}
							{dayjs(record.createdAt).format('hh:mm a')}
						</span>
						<span style={{ color: '#74788D', fontSize: '10px' }}>by {record.createdBy}</span>
					</div>
				);
			},
		},
		{
			header: 'Last access',
			id: 'lastAccess',
			cell: ({ row }: any) => {
				const record = row.original as DataType;
				return (
					<div className="flex column">
						<span>{dayjs(record.lastAccess).format('YYYY-MM-DD')}</span>
						<span style={{ color: '#74788D', fontSize: '10px' }}>
							{dayjs(record.lastAccess).format('hh:mm a')}
						</span>
					</div>
				);
			},
		},
		{
			header: 'Actions',
			id: 'actions',
			cell: ({ row }: any) => {
				const record = row.original as AllUser;
				const isSelf = record.userId === loggedUserId;
				const isTargetSuperAdmin = record.role === 'SuperAdmin';
				const canDelete = !isSelf && !(userRole === 'Administrator' && isTargetSuperAdmin);
				return (
					<div className="flex items-center">
						<Tooltip title="Edit">
							<Button
								size="small"
								showIcon={true}
								showLabel={false}
								iconName="edit-alt"
								style={{
									borderWidth: 0,
									borderRadius: '4px',
									color: '#74788D',
									height: 24,
									width: 24,
									padding: 0,
									backgroundColor: 'white',
								}}
								onClick={() => {
									setSelectedUser(record);
									setAddModalUser(true);
									setIsNewUser(false);
								}}
							/>
						</Tooltip>
						{canDelete && (
							<>
								<div className="mr-1" />
								<Tooltip title="Delete">
									<Button
										size="small"
										showIcon={true}
										showLabel={false}
										iconName="trash-alt"
										style={{
											borderWidth: 0,
											borderRadius: '4px',
											color: '#74788D',
											height: 24,
											width: 24,
											padding: 0,
											backgroundColor: 'white',
										}}
										onClick={() => {
											setSelectedUser(record);
											setShowConfirmDelete(true);
										}}
									/>
								</Tooltip>
							</>
						)}
					</div>
				);
			},
		},
	];

	const handleGetUsers = useCallback(() => {
		const oui = JSON.parse(localStorage.getItem('security') || '{}').organizationId;
		const name = debouncedName || null;

		// Check if filters changed to reset pagination
		const filtersChanged =
			prevFiltersRef.current.debouncedName !== debouncedName ||
			prevFiltersRef.current.role !== role;

		const pageIndex = filtersChanged ? 0 : paginationState.pageIndex;

		if (filtersChanged) {
			setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
			prevFiltersRef.current = { debouncedName, role };
		}

		const res = userService.getAllUsers(oui, pageIndex + 1, paginationState.pageSize, name, role);

		res
			.then((response) => {
				setTotalRows(response.totalRecords);
				setUsers(response.responseContentDTO);
			})
			.catch((error) => {
				console.error(error);
			});
	}, [paginationState.pageIndex, paginationState.pageSize, debouncedName, role]);

	useEffect(() => {
		handleGetUsers();
	}, [handleGetUsers]);

	useEffect(() => {
		if (!addModalUser) {
			handleGetUsers();
			setSelectedUser(null);
		}
	}, [addModalUser]);

	useEffect(() => {
		const resRole = userService.getRole();
		resRole
			.then((response) => {
				setRoles(response);
			})
			.catch((error) => {
				console.error(error);
			});

		const resModules = userService.getModules();
		resModules
			.then((response) => {
				setModules(response);
			})
			.catch((error) => {
				console.error(error);
			});

		if (userRole == 'SuperAdmin') {
			const resOrganizations = userService.getOrganizations();
			resOrganizations
				.then((response) => {
					setOrganizations(response);
				})
				.catch((error) => {
					console.error(error);
				});
		}
	}, []);

	const handleOk = () => {
		setAddModalUser(false);
	};

	const handleCancel = () => {
		setAddModalUser(false);
	};

	return (
		<div>
			<div className="text-title-page">User Manager</div>
			<div className="flex justify-between" style={{ marginTop: '2rem' }}>
				<div className="flex">
					<Search
						size="small"
						placeholder="Search"
						value={searchValue}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							setSearchValue(e.target.value);
						}}
						onClear={() => {
							setSearchValue('');
						}}
						showClearButton
					/>

					<div style={{ marginLeft: 30, width: 160 }}>
						<Combobox
							items={(roles || []).map((r: any) => ({
								value: String(r.value),
								label: r.label,
							}))}
							onValueChange={(val) => setRole(val)}
							onClear={() => setRole(null)}
							size="small"
						>
							<ComboboxInput placeholder="Role" />
							<ComboboxContent>
								<ComboboxEmpty>No roles found.</ComboboxEmpty>
								<ComboboxList>
									{(item) => (
										<ComboboxItem key={item.value} value={item}>
											{item.label}
										</ComboboxItem>
									)}
								</ComboboxList>
							</ComboboxContent>
						</Combobox>
					</div>
					{userRole == 'SuperAdmin' ? (
						<div style={{ marginLeft: 30, width: 180 }}>
							<Combobox
								items={(organizations || []).map((o: any) => ({
									value: String(o.value),
									label: o.label,
								}))}
								onValueChange={(val) => setOrganization(val)}
								onClear={() => setOrganization(null)}
								size="small"
							>
								<ComboboxInput placeholder="Organization" />
								<ComboboxContent>
									<ComboboxEmpty>No organizations found.</ComboboxEmpty>
									<ComboboxList>
										{(item) => (
											<ComboboxItem key={item.value} value={item}>
												{item.label}
											</ComboboxItem>
										)}
									</ComboboxList>
								</ComboboxContent>
							</Combobox>
						</div>
					) : (
						''
					)}
				</div>

				<div>
					<Button
						variant="primary"
						size="small"
						label="New user"
						iconName="plus-circle"
						iconColor="white"
						onClick={() => {
							setAddModalUser(true);
							setIsNewUser(true);
						}}
					/>
				</div>
			</div>
			<div className="mt-3">
				<DataGrid
					data={users}
					columns={gridColumns}
					pagination={{
						enabled: true,
						manualPagination: true,
						rowCount: totalRows,
						defaultPageSize: 10,
						pageSizeOptions: [10, 20, 50],
					}}
					stickyHeader={true}
					maxHeight="calc(100vh - 320px)"
					paginationState={paginationState}
					onPaginationChange={setPaginationState}
				/>
			</div>
			{addModalUser && (
				<ModalUser
					addModalUser={addModalUser}
					handleOk={handleOk}
					handleCancel={handleCancel}
					setAddModalUser={setAddModalUser}
					roles={roles}
					modules={modules}
					organizations={organizations}
					handleGetUsers={handleGetUsers}
					isNewUser={isNewUser}
					selectedUser={selectedUser}
					setSelectedUser={setSelectedUser}
				/>
			)}
			<ConfirmationModal
				title="Delete User?"
				message="Are you sure to delete it?"
				showModal={showConfirmDelete}
				onAccept={async () => {
					if (!selectedUser) return;

					if (selectedUser.role === 'Administrator') {
						const adminRoleId = roles?.find((r: any) => r.label === 'Administrator')?.value;
						if (adminRoleId) {
							const oui = securityData.organizationId;
							const res = await userService.getAllUsers(oui, 1, 1, null, adminRoleId);
							if (res.totalRecords <= 1) {
								notification.warning({
									message: 'Action not allowed',
									description: 'Cannot delete the only administrator in the organization.',
								});
								setShowConfirmDelete(false);
								return;
							}
						}
					}

					userService.deleteUser(selectedUser).then(() => {
						notification.success({
							message: 'Successful',
							description: 'User deleted successfully',
						});
						handleGetUsers();
					});
					setShowConfirmDelete(false);
				}}
				oncancel={() => {
					setShowConfirmDelete(false);
				}}
			/>
		</div>
	);
};

export default UserView;
